import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'es-toolkit';
import { hasAnyBankingField, isValidBankCode, isValidIban, isValidSwift } from '@insio/validation';
import { useTranslation } from '../../../../providers/UIProvider';
import { IBankingDetails } from '@insio/shared-types';

export interface UseBankingDetailsFormStateOptions {
  open: boolean;
  bankingDetails?: IBankingDetails | null;
  /** Optional debounced notifier — fires after `debounceMs` of idle between
   * `setField` calls. Stays in sync with the latest closure without
   * resetting the timer. */
  onFieldChange?: (field: keyof IBankingDetails, value: string) => void;
  /** Debounce window for `onFieldChange`. Defaults to 300ms. */
  debounceMs?: number;
}

export interface UseBankingDetailsFormStateResult {
  formData: IBankingDetails;
  setField: (field: keyof IBankingDetails, value: string) => void;
  reset: () => void;
  swiftWarning: string | null;
  bankCodeWarning: string | null;
  ibanWarning: string | null;
  hasAnyWarning: boolean;
  formError: string | null;
  setFormError: (err: string | null) => void;
  /** Run the "at least one field" validation. Returns true if valid. Sets
   * `formError` on failure. */
  validateAtLeastOneField: () => boolean;
}

const EMPTY: IBankingDetails = {
  bankAccount: '',
  bankName: '',
  iban: '',
  swift: '',
  bankCode: '',
  varSymb: '',
  constSymb: '',
  specSymb: ''
};

export function useBankingDetailsFormState({
  open,
  bankingDetails,
  onFieldChange,
  debounceMs = 300
}: UseBankingDetailsFormStateOptions): UseBankingDetailsFormStateResult {
  const t = useTranslation('modules.modals.editBankingDetails');
  const [formData, setFormData] = useState<IBankingDetails>(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);

  // Latest-callback ref so debounce isn't recreated when a new callback closure
  // is passed each render — recreating would reset the debounce timer.
  const onFieldChangeRef = useRef(onFieldChange);
  useEffect(() => {
    onFieldChangeRef.current = onFieldChange;
  });

  const debouncedNotify = useMemo(
    () =>
      debounce((field: keyof IBankingDetails, value: string) => {
        onFieldChangeRef.current?.(field, value);
      }, debounceMs),
    [debounceMs]
  );

  useEffect(() => () => debouncedNotify.cancel(), [debouncedNotify]);

  useEffect(() => {
    if (open && bankingDetails) {
      setFormData({
        bankAccount: bankingDetails.bankAccount || '',
        bankName: bankingDetails.bankName || '',
        iban: bankingDetails.iban || '',
        swift: bankingDetails.swift || '',
        bankCode: bankingDetails.bankCode || '',
        varSymb: bankingDetails.varSymb || '',
        constSymb: bankingDetails.constSymb || '',
        specSymb: bankingDetails.specSymb || ''
      });
      setFormError(null);
      debouncedNotify.cancel();
    }
  }, [open, bankingDetails, debouncedNotify]);

  const setField = useCallback(
    (field: keyof IBankingDetails, value: string) => {
      setFormData(prev => ({ ...prev, [field]: value }));
      debouncedNotify(field, value);
    },
    [debouncedNotify]
  );

  const reset = useCallback(() => {
    setFormData(EMPTY);
    setFormError(null);
    debouncedNotify.cancel();
  }, [debouncedNotify]);

  const swiftWarning = isValidSwift(formData.swift) ? null : t('warnings.invalidSwift');
  const bankCodeWarning = isValidBankCode(formData.bankCode) ? null : t('warnings.invalidBankCode');
  const ibanWarning = isValidIban(formData.iban) ? null : t('warnings.invalidIban');
  const hasAnyWarning = !!(swiftWarning || bankCodeWarning || ibanWarning);

  const validateAtLeastOneField = useCallback((): boolean => {
    if (!hasAnyBankingField(formData)) {
      setFormError(t('errors.atLeastOneField'));
      return false;
    }
    setFormError(null);
    return true;
  }, [formData, t]);

  return {
    formData,
    setField,
    reset,
    swiftWarning,
    bankCodeWarning,
    ibanWarning,
    hasAnyWarning,
    formError,
    setFormError,
    validateAtLeastOneField
  };
}
